// Generic list + create / edit / delete page for one backend resource.
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { useMeta, type ResourceMeta } from '@/hooks/meta';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/ui/badge';
import { PageHeader, DataTable, Pager, Loading, ErrorBox, type Column } from '@/components/common';
import { ResourceForm } from '@/components/ResourceForm';
import { toast } from '@/store/toast';
import { humanize, fieldLabel, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';

export interface ResourcePageProps {
  path: string;
  title: string;
  description?: string;
  columns: (string | Column<any>)[];
  formFields?: string[];
  fixed?: Record<string, unknown>;          // values forced on create and used as list filters
  rowActions?: (row: any, refresh: () => void) => React.ReactNode;
  headerActions?: React.ReactNode;
  embedded?: boolean;                        // inside another page: no big header
  onRowClick?: (row: any) => void;
}

const BADGE_COLS = new Set(['status', 'state', 'dial_method', 'scope', 'type', 'role', 'category']);

export function ResourcePage(p: ResourcePageProps) {
  const meta = useMeta();
  const qc = useQueryClient();
  const [q, setQ] = React.useState('');
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [offset, setOffset] = React.useState(0);
  const [editing, setEditing] = React.useState<any | null | undefined>(undefined);   // undefined closed, null new
  const limit = 25;
  const query = { q, ...filters, ...(p.fixed as Record<string, string> | undefined), limit, offset };
  const list = useQuery({ queryKey: [p.path, query], queryFn: () => api<{ rows: any[]; total: number }>(`/${p.path}`, { query }) });
  const m: ResourceMeta | undefined = meta.data?.[p.path];

  const refresh = () => { qc.invalidateQueries({ queryKey: [p.path] }); qc.invalidateQueries({ queryKey: ['ref', p.path] }); };

  async function save(values: Record<string, unknown>) {
    if (editing) await api(`/${p.path}/${editing.id}`, { method: 'PATCH', body: values });
    else await api(`/${p.path}`, { method: 'POST', body: values });
    toast.ok(editing ? t('Saved') : t('Created'));
    setEditing(undefined);
    refresh();
  }

  async function remove(row: any) {
    if (!confirm(t('Delete this {{name}}?', { name: humanize(p.path).toLowerCase().replace(/s$/, '') }))) return;
    try { await api(`/${p.path}/${row.id}`, { method: 'DELETE' }); toast.ok(t('Deleted')); refresh(); }
    catch (e) { toast.error(errorText(e)); }
  }

  const columns: Column<any>[] = p.columns.map((c) => typeof c !== 'string' ? c : {
    key: c, label: fieldLabel(c),
    render: BADGE_COLS.has(c) ? (r: any) => <StatusBadge value={r[c]} /> : undefined,
  });
  const enumFilters = (m?.filters ?? []).filter((f) => m?.fields[f]?.type === 'enum' && !(p.fixed && f in p.fixed));

  return (
    <div>
      {p.embedded ? (
        <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">{p.title}</h2>
          {m?.creatable && <Button size="sm" onClick={() => setEditing(null)}><Plus /> {t('Add')}</Button>}</div>
      ) : (
        <PageHeader title={p.title} description={p.description}>
          {p.headerActions}
          {m?.creatable && <Button onClick={() => setEditing(null)} data-testid="add-button"><Plus /> {t('Add')}</Button>}
        </PageHeader>
      )}
      <div className="mb-3 flex flex-wrap gap-2">
        {m && m.search.length > 0 && (
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8" placeholder={t('Search…')} value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} />
          </div>
        )}
        {enumFilters.map((f) => (
          <Select key={f} className="w-44" value={filters[f] ?? ''} onChange={(e) => { setFilters((s) => ({ ...s, [f]: e.target.value })); setOffset(0); }}>
            <option value="">{t('All {{name}}', { name: fieldLabel(f).toLowerCase() })}</option>
            {m!.fields[f].values!.map((v) => <option key={v} value={v}>{enumLabel(v)}</option>)}
          </Select>
        ))}
      </div>
      <ErrorBox error={list.error ?? meta.error} />
      {list.isLoading ? <Loading /> : (
        <DataTable columns={columns} rows={list.data?.rows} onRowClick={p.onRowClick}
          actions={(r) => (<>
            {p.rowActions?.(r, refresh)}
            {m?.editable && <Button size="icon" variant="ghost" aria-label={t('Edit')} onClick={() => setEditing(r)}><Pencil /></Button>}
            {m?.deletable && <Button size="icon" variant="ghost" aria-label={t('Delete')} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button>}
          </>)} />
      )}
      <Pager offset={offset} limit={limit} total={list.data?.total} onChange={setOffset} />
      <Dialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)}>
        {m && editing !== undefined && (
          <DialogContent title={editing ? t('Edit {{name}}', { name: p.title.replace(/s$/, '').toLowerCase() }) : t('New {{name}}', { name: p.title.replace(/s$/, '').toLowerCase() })} className="max-w-2xl">
            <ResourceForm meta={m} initial={editing} fields={p.formFields} fixed={p.fixed} onSubmit={save} onCancel={() => setEditing(undefined)} />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
